import { ReactElement } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useFlags } from '@equipmentshare/feature-flags'
import DcAdminRoutes from './index.routes'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { DcAdminProvider, useDcAdminContext } from './contexts/DcAdminContext'
import { APP_HEADER_HEIGHT } from '../helpers/utils'

const DC_ADMIN_HEADER_HEIGHT = 56

function DcAdminHeader() {
	const { selectedDcId, setSelectedDcId, dcs, loading } = useDcAdminContext()
	const navigate = useNavigate()
	const location = useLocation()
	const flags = useFlags()
	const pickOrdersEnabled = Boolean( flags.costentoryEnablePickOrders )

	const activeTab = location.pathname.includes( '/pick-orders' )
		? '/dc-admin/pick-orders'
		: location.pathname.includes( '/mtt' )
			? '/dc-admin/mtt'
			: '/dc-admin/fulfillment-orders'

	return (
		<Box
			sx={{
				height: DC_ADMIN_HEADER_HEIGHT,
				display: 'flex',
				alignItems: 'center',
				borderBottom: '2px solid',
				borderColor: 'primary.main',
				bgcolor: 'dcAdminHeader.background',
				px: 3,
				gap: 3,
				flexShrink: 0,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
				<Box
					sx={{
						bgcolor: 'primary.main',
						color: 'white',
						px: 1,
						py: 0.25,
						borderRadius: 0.5,
						fontSize: 11,
						fontWeight: 700,
						letterSpacing: '0.08em',
						textTransform: 'uppercase',
						whiteSpace: 'nowrap',
					}}
				>
					DC Admin
				</Box>
				<FormControl size="small" sx={{ minWidth: 260 }} disabled={loading || dcs.length === 0}>
					<InputLabel sx={{ color: 'dcAdminHeader.contrastTextMuted', '&.Mui-focused': { color: 'primary.light' } }}>
						Distribution Center
					</InputLabel>
					<Select
						value={selectedDcId ?? ''}
						label="Distribution Center"
						onChange={( e: SelectChangeEvent ) => setSelectedDcId( e.target.value || null )}
						sx={{
							color: 'white',
							'.MuiOutlinedInput-notchedOutline': { borderColor: 'dcAdminHeader.contrastBorder' },
							'&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'dcAdminHeader.contrastTextSubtle' },
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.light' },
							'.MuiSvgIcon-root': { color: 'dcAdminHeader.contrastTextMuted' },
						}}
					>
						{dcs.map( ( dc ) => (
							<MenuItem key={dc.id} value={dc.id}>{dc.name}</MenuItem>
						) )}
					</Select>
				</FormControl>
			</Box>

			<Tabs
				value={activeTab}
				onChange={( _, val ) => navigate( val )}
				sx={{
					ml: 1,
					'.MuiTab-root': { color: 'dcAdminHeader.contrastTextSubtle', minHeight: DC_ADMIN_HEADER_HEIGHT, textTransform: 'none', fontSize: 14 },
					'.Mui-selected': { color: 'white !important' },
					'.MuiTabs-indicator': { backgroundColor: 'primary.light', height: 3 },
				}}
			>
				<Tab label="Fulfillment Orders" value="/dc-admin/fulfillment-orders" />
				<Tab label="MTT Management" value="/dc-admin/mtt" />
				{/* Tabs reads its children directly, so an unrendered tab has to be
				    omitted from the array rather than returning null. */}
				{pickOrdersEnabled && <Tab label="Pick Orders" value="/dc-admin/pick-orders" />}
			</Tabs>

			<Box sx={{ ml: 'auto' }} />
		</Box>
	)
}

function DcAdminLayout(): ReactElement {
	const totalHeaderHeight = APP_HEADER_HEIGHT + DC_ADMIN_HEADER_HEIGHT

	return (
		<Box sx={{ height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`, display: 'flex', flexDirection: 'column' }}>
			<DcAdminHeader />
			<Box sx={{ flex: 1, minHeight: 0, height: `calc(100vh - ${totalHeaderHeight}px)` }}>
				<DcAdminRoutes />
			</Box>
		</Box>
	)
}

export default function DcAdmin(): ReactElement {
	return (
		<DcAdminProvider>
			<DcAdminLayout />
		</DcAdminProvider>
	)
}
